import { Navigate, useLocation } from "react-router-dom";
import useAuth from "../context/useAuth.js";
import { hasRole } from "../utils/permissions.js";
import { Spinner } from "./ui/Feedback.jsx";

/**
 * Client-side gate for every app route. The backend's `protect` middleware is the
 * real security boundary — this only avoids rendering screens the user cannot
 * populate, and preserves the attempted URL for post-login redirect.
 */
export default function ProtectedRoute({ children }) {
  const { isAuthenticated, isChecking } = useAuth();
  const location = useLocation();

  if (isChecking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <Spinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}

/**
 * Role gate for the admin-only modules — Reports, Payments, Expenses and the
 * staff roster. Composes with `ProtectedRoute` (which wraps the whole shell), so
 * it only has to answer "is this signed-in user allowed here?".
 *
 * A staff login that lands on one of these URLs is bounced to the dashboard
 * rather than the login screen: they are authenticated, just not entitled.
 */
export function RequireRole({ roles, children }) {
  const { user, isAuthenticated, isChecking } = useAuth();
  const location = useLocation();

  if (isChecking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <Spinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin" replace state={{ from: location }} />;
  }

  if (!hasRole(user, roles)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/** Inverse gate: keeps an authenticated user off the login screen. */
export function PublicOnlyRoute({ children }) {
  const { isAuthenticated, isChecking } = useAuth();

  if (isChecking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <Spinner />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
